import first from 'lodash/first';
import isEmpty from 'lodash/isEmpty';
import orderBy from 'lodash/orderBy';
import type { Calorific } from 'types/Calorific';
import type { Diet } from 'types/Diet';
import type { OrderForm, Package } from 'types/OrderForm';
import type { Variant } from 'types/Variant';
import { orderByPositionName } from '@utils/helpers';

type OfferSelection = {
  calorific: Calorific | undefined;
  diet: Diet | undefined;
  offerPackage: Package | undefined;
  variant: Variant | undefined;
};

export const filterVariantsWithoutCalorifics = <
  T extends Pick<Variant, 'calories'>
>(
  variants: T[] = []
) => variants.filter(({ calories }) => !isEmpty(calories));

export const filterOfferWithoutCalorifics = (orderForm: OrderForm) => {
  const { diets = [], packages = [], packageLineFilters = [] } = orderForm;

  const orderableDiets = diets
    .map(diet => ({
      ...diet,
      variants: filterVariantsWithoutCalorifics(diet.variants),
    }))
    .filter(({ variants }) => !isEmpty(variants));
  const orderableDietsIds = orderableDiets.map(({ id }) => id);

  const orderablePackages = packages
    .map(offerPackage => ({
      ...offerPackage,
      activeDietsIds: (offerPackage.activeDietsIds ?? []).filter(id =>
        orderableDietsIds.includes(id)
      ),
    }))
    .filter(({ activeDietsIds }) => !isEmpty(activeDietsIds));
  const orderablePackagesIds = orderablePackages.map(({ id }) => id);

  const orderablePackageLineFilters = packageLineFilters
    .map(packageLineFilter => ({
      ...packageLineFilter,
      activePackagesIds: (packageLineFilter.activePackagesIds ?? []).filter(
        id => orderablePackagesIds.includes(id)
      ),
    }))
    .filter(({ activePackagesIds }) => !isEmpty(activePackagesIds));

  return {
    ...orderForm,
    diets: orderableDiets,
    packages: orderablePackages,
    packageLineFilters: orderablePackageLineFilters,
  };
};

const getInitialPackage = (packages: Package[]) => {
  const packagesWithActiveDiets = packages.filter(
    ({ activeDietsIds }) => !isEmpty(activeDietsIds)
  );

  return first(
    orderBy(packagesWithActiveDiets, ['position', 'name'], ['asc', 'desc'])
  );
};

const getInitialDiet = (
  diets: Diet[],
  packages: Package[],
  offerPackage: Package | undefined
) => {
  if (!isEmpty(packages)) {
    const { activeDietsIds = [] } = offerPackage ?? {};
    const packageDiets = diets.filter(({ id }) => activeDietsIds.includes(id));

    return first(orderByPositionName(packageDiets)) as Diet | undefined;
  }

  return first(diets);
};

export const getInitialOfferSelection = (
  diets: Diet[] = [],
  packages: Package[] = []
): OfferSelection => {
  const offerPackage = getInitialPackage(packages);
  const diet = getInitialDiet(diets, packages, offerPackage);
  const variant = first(orderByPositionName(diet?.variants)) as
    | Variant
    | undefined;
  const calorific = first(orderByPositionName(variant?.calories)) as
    | Calorific
    | undefined;

  return { calorific, diet, offerPackage, variant };
};
